import { Injectable } from '@angular/core';
import { KnowledgeBaseService } from 'src/app/modules/olp-api/services/kb/kb.service';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import {
  ActivityFolder,
  ActivityState,
  ActivityStateFolderManager,
  IncompleteActivityState,
} from '../..';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { Activity } from 'src/app/shared/interfaces';
import {
  ProgressNodeFocusEnum,
  ProgressNodeStatusEnum,
  ProgressNodeTypeEnum,
} from 'src/app/modules/progress-bar/enums';
import { IProgressNodeData } from 'src/app/modules/progress-bar/interfaces/progress-node-data.interface';
import { ActivityObjectTypes } from 'src/app/shared/enums/activity-object-types';
import { ActivityManifestObject } from 'src/app/shared/interfaces/activity-manifest-object';

@Injectable({
  providedIn: 'root',
})
export class ActivityStateFolderService {
  activityStateManager!: ActivityStateFolderManager;
  constructor(
    private kbService: KnowledgeBaseService,
    private featureFlagService: FeatureFlagService
  ) {
    this.activityStateManager = new ActivityStateFolderManager(
      this.featureFlagService.isFlagEnabled(FeatureFlags.USE_KB)
        ? this.kbService.kb
        : undefined
    );
  }

  async addIncompleteActivity(
    activityId: string,
    incompleteActivity: IncompleteActivityState
  ): Promise<void> {
    return this.activityStateManager.addIncompleteActivity(
      activityId,
      incompleteActivity
    );
  }

  async getIncompleteActivity(
    activityId: string
  ): Promise<IncompleteActivityState | undefined> {
    return this.activityStateManager.getIncompleteActivity(activityId);
  }

  async getIncompleteActivities(): Promise<IncompleteActivityState[]> {
    return this.activityStateManager.getIncompleteActivities();
  }

  async removeIncompleteActivity(activityId: string): Promise<void> {
    return this.activityStateManager.removeIncompleteActivity(activityId);
  }

  async addCompleteActivity(completedActivity: ActivityState): Promise<void> {
    return this.activityStateManager.addCompleteActivity(completedActivity);
  }

  async getCompleteActivities(activityId: string): Promise<ActivityState[]> {
    return this.activityStateManager.getCompleteActivities(activityId);
  }

  async getActivityFolders(): Promise<ActivityFolder[]> {
    return this.activityStateManager.getActivityFolders();
  }

  async getActivityFolder(activityId: string): Promise<ActivityFolder> {
    return this.activityStateManager.getActivityFolder(activityId);
  }

  async saveActivityFolder(activityFolder: ActivityFolder): Promise<void> {
    this.activityStateManager.saveActivityFolder(activityFolder);
  }

  /**
   * Creates an activity state on the first step based on the activity provided
   * @param activity The activity to create the default activity state for
   * @returns An Activity state on the first step
   */
  createDefaultActivityState(activity: Activity): IncompleteActivityState {
    return {
      activityId: activity.id,
      startTime: new Date().toISOString(),
      secondsToComplete: 0,
      lastOpened: Date.now().toString(),
      isAssignment: false,
      progressNodes: this.createProgressNodesFromManifest(
        activity.activityManifest
      ),
      currentStep: -1,
      populationResponses: [],
      clickstreamInteractions: [],
      currentActivityOid: -1,
    };
  }

  private createProgressNodesFromManifest(
    manifest: ActivityManifestObject[]
  ): IProgressNodeData[] {
    const nodes: IProgressNodeData[] = [];
    let activityCreationIndex = 0;
    let activityCreationQuestionIndex = 1;
    for (let i = 0; i < manifest.length; i++) {
      const node = this.getNodeFromManifestObject(
        manifest[i],
        activityCreationIndex,
        activityCreationQuestionIndex,
        i === 0 ? ProgressNodeFocusEnum.active : ProgressNodeFocusEnum.inactive,
        ProgressNodeStatusEnum.none
      );
      activityCreationIndex++;
      if (node) {
        if (node.type === ProgressNodeTypeEnum.progressNodeComp) {
          activityCreationQuestionIndex++;
        }
        nodes.push(node);
      }
    }
    return nodes;
  }

  private getNodeFromManifestObject(
    manifestObject: ActivityManifestObject,
    activityCreationIndex: number,
    activityCreationQuestionIndex: number,
    focus?: number,
    status?: number
  ): IProgressNodeData | undefined {
    switch (manifestObject.type) {
      case ActivityObjectTypes.VIDEO:
        return {
          index: activityCreationIndex,
          type: ProgressNodeTypeEnum.videoNodeComp,
          data: {},
          focus: focus || ProgressNodeFocusEnum.inactive,
          status: 0,
        };
      case ActivityObjectTypes.QUESTION: {
        return {
          index: activityCreationIndex,
          type: ProgressNodeTypeEnum.progressNodeComp,
          data: { text: activityCreationQuestionIndex.toString() },
          focus: focus || ProgressNodeFocusEnum.inactive,
          status: status || ProgressNodeStatusEnum.none,
        };
      }
      default:
        return undefined;
    }
  }
}
